import React from 'react';
import { Helmet } from 'react-helmet-async';

// Site URL constant for canonical URLs
const SITE_URL = 'https://www.l-art.be';

/**
 * Normalize URL with trailing slash for canonical URLs
 * Excludes: root, files with extensions, special paths
 */
const normalizeCanonicalUrl = (url) => {
  if (!url || url === '/' || url === '') return url;
  
  // Don't add trailing slash to files or special paths
  const fileExtensionRegex = /\.[a-z0-9]{2,5}$/i;
  if (fileExtensionRegex.test(url)) return url;
  
  // Don't add trailing slash to admin/api paths
  if (url.includes('/api') || url.includes('/admin')) return url;
  
  // Add trailing slash if missing
  if (!url.endsWith('/')) {
    return `${url}/`;
  }
  return url;
};

export const SEO = ({
  title = "L'ART | Handgemaakte Manden uit België",
  description = "Ontdek de tijdloze schoonheid van handgemaakte manden. Elk stuk is uniek en met liefde gemaakt in België uit 80% gerecycleerd katoen.",
  keywords = "handgemaakte manden, rieten manden, belgische manden, duurzame manden, gerecycleerd katoen, artisanale manden, opbergmanden, decoratieve manden",
  image = "/logo-lart.png",  // Default OG image
  url = "",
  type = "website",
  author = "L'ART Belgium",
  canonical,
  schema,
  noIndex = false  // New: for filtered pages
}) => {
  const siteUrl = SITE_URL; // Use constant for production canonical URLs
  
  // Normalize URL with trailing slash
  const normalizedUrl = url ? normalizeCanonicalUrl(url) : '';
  const fullUrl = normalizedUrl ? `${siteUrl}${normalizedUrl}` : siteUrl;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Robots directive based on noIndex prop
  const robotsContent = noIndex ? 'noindex, follow' : 'index, follow';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="nl-BE" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL - must be absolute */}
      {canonical && <link rel="canonical" href={canonical} />}
      {!canonical && <link rel="canonical" href={fullUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="L'ART" />
      <meta property="og:locale" content="nl_BE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Robots - noindex for filtered pages */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="language" content="Dutch" />
      <meta name="geo.region" content="BE" />
      <meta name="geo.placename" content="België" />

      {/* Schema.org Structured Data */}
      {schema && (
        Array.isArray(schema) ? (
          schema.map((schemaItem, index) => (
            <script key={`schema-${index}`} type="application/ld+json">
              {JSON.stringify(schemaItem)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )
      )}
    </Helmet>
  );
};

// Export SITE_URL for use in other components
export { SITE_URL };

// Organization Schema Generator
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "L'ART",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo512.png`,
    "description": "Handgemaakte artisanale manden uit België, gemaakt van 80% gerecycleerd katoen",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Oudenaarde",
      "addressCountry": "BE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@l-art.be",
      "contactType": "customer service",
      "areaServed": "BE"
    },
    "sameAs": [
      "https://www.instagram.com/lart.be",
      "https://www.facebook.com/lart.belgium"
    ]
  };
};

// Product Schema Generator - Enriched with full product data
export const generateProductSchema = (product) => {
  const productUrl = `${SITE_URL}/product/${product.id}`;
  
  // Build image array - prefer cloudinary_images, fallback to images, then single image
  const imageArray = (() => {
    if (product.cloudinary_images?.length) {
      return product.cloudinary_images;
    }
    if (product.images?.length) {
      return product.images;
    }
    if (product.image) {
      return [product.image];
    }
    return [];
  })();
  
  // Build additionalProperty array for dimensions
  const additionalProperties = [
    product.height_cm && {
      "@type": "PropertyValue",
      "name": "Hoogte",
      "value": `${product.height_cm} cm`
    },
    product.diameter_cm && {
      "@type": "PropertyValue",
      "name": "Diameter", 
      "value": `${product.diameter_cm} cm`
    }
  ].filter(Boolean);
  
  // Format price as string with 2 decimals, no currency symbol
  const formattedPrice = typeof product.price === 'number' 
    ? product.price.toFixed(2) 
    : String(product.price);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": productUrl,
    "name": product.name,
    "description": product.description,
    "image": imageArray,
    "brand": {
      "@type": "Brand",
      "name": "L'ART"
    },
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "priceCurrency": "EUR",
      "price": formattedPrice,
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "L'ART"
      }
    }
  };
  
  // Add optional fields only if they have values
  if (product.category) {
    schema.category = product.category;
  }
  if (product.color) {
    schema.color = product.color;
  }
  if (product.material) {
    schema.material = product.material;
  }
  if (additionalProperties.length > 0) {
    schema.additionalProperty = additionalProperties;
  }
  
  return schema;
};

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
    }))
  };
};
